<template>
  <div class="op-page">
    <!-- ============================================================
         A. 权限不足态
         ============================================================ -->
    <div class="op-denied" v-if="authDenied">
      <div class="op-denied__ic"><van-icon name="warning-o" /></div>
      <div class="op-denied__t">无运营端访问权限</div>
      <div class="op-denied__m">{{ authMsg }}</div>
      <div class="op-denied__card">
        <div class="op-denied__row"><span>账号</span><b>{{ profile.name || '-' }}</b></div>
        <div class="op-denied__row"><span>角色</span><b>{{ roleText || '-' }}</b></div>
        <div class="op-denied__row"><span>部门</span><b>{{ deptText || '-' }}</b></div>
      </div>
      <div class="op-denied__hint">
        运营端开放给：运营人员（角色「运营」）/ 运营部门成员 / 管理员。如需开通请联系系统管理员。
      </div>
      <van-button block type="primary" round @click="loadProfile">重新检测</van-button>
      <van-button block plain round @click="onLogout">退出登录</van-button>
    </div>

    <template v-else>
      <!-- ============================================================
           B. 快捷入口 Tab（原「首页」内容，【P176】房态占用 Tab0 后并入此处）
              画像 + KPI 看板 + 快捷九宫格
              【P235 修正】★ 本块判据 tabIndex===4 —— TABS 只有 5 项（索引 0~4）：
                0房态 1会议室订单 2增值订单 3采购单 4快捷入口。
                之前误写成 5 ⇒ **快捷入口页永远不渲染**（Tab 第 5 项点不动、九宫格不出现）。
                消息中心已改为 subKey 子页，不再占 Tab 索引，两者不再冲突。
           ============================================================ -->
      <template v-if="!subKey && tabIndex === 4">
        <!-- 【P112】下拉刷新：重拉 KPI 看板 + 未读角标 -->
        <van-pull-refresh v-model="homeRefreshing" @refresh="onHomeRefresh">
        <div class="op-hero">
          <div class="op-hero__top">
            <span class="op-avatar">{{ avatarText }}</span>
            <div class="op-hero__id">
              <div class="op-hero__name">{{ profile.name || '运营人员' }}</div>
              <div class="op-hero__dept">{{ deptText || '运营' }}</div>
            </div>
            <span class="op-hero__role">{{ roleText || '运营' }}</span>
          </div>
          <div class="op-kpis">
            <div class="op-kpi" @click="openSub('message')">
              <div class="op-kpi__n">{{ unread.total }}<span class="op-kpi__dot" v-if="unread.total > 0"></span></div>
              <div class="op-kpi__l">未读消息</div>
            </div>
            <div class="op-kpi">
              <div class="op-kpi__n">{{ dash.room.service_todo || 0 }}</div>
              <div class="op-kpi__l">待店长确认</div>
            </div>
            <div class="op-kpi">
              <div class="op-kpi__n">{{ dash.room.today || 0 }}</div>
              <div class="op-kpi__l">今日到场</div>
            </div>
          </div>
        </div>

        <!-- 快捷入口（首项＝消息中心） -->
        <div class="op-quick">
          <div class="op-quick__hd"><span>快捷入口</span></div>
          <div class="op-grid">
            <div class="op-tile" v-for="q in quickItems" :key="q.key" @click="openSub(q.key)">
              <div class="op-tile__ic">
                <span class="op-tile__svg" v-html="q.icon"></span>
                <span class="op-tile__dot" v-if="q.key === 'message' && unread.total > 0">{{ unread.total > 99 ? '99+' : unread.total }}</span>
              </div>
              <div class="op-tile__label">{{ q.label }}</div>
            </div>
          </div>
          <div class="op-tip">数据为全公司口径；会议室订单支持【推送待办】与【完成服务】</div>
        </div>

        <!-- 【P196】账号：退出登录
             ★ 与销售端 client.vue 的「账号」区块 1:1 对齐（同一套 .acct-* 结构与视觉）：
               .op-quick__hd / .acct-list / .acct-row（图标 + 文案 + 箭头）
             ★ 运营端此前无任何退出入口（onLogout 仅挂在「权限不足态」），本次补齐。
             ★ 仅出现在「快捷入口」Tab（tabIndex===5）内，与用户要求一致。 -->
        <div class="op-quick op-quick--acct">
          <div class="op-quick__hd op-quick__hd--plain"><span>账号</span></div>
          <div class="acct-list">
            <div class="acct-row" @click="askLogout">
              <span class="acct-row__ic">
                <svg viewBox="0 0 24 24">
                  <path d="M15 4h3.5v16H15" />
                  <path d="M10.5 8 6.5 12l4 4" />
                  <path d="M6.5 12H15" />
                </svg>
              </span>
              <span class="acct-row__txt">
                <span class="acct-row__label">退出登录</span>
                <span class="acct-row__sub">退出后需重新登录</span>
              </span>
              <van-icon name="arrow" class="acct-row__arrow" />
            </div>
          </div>
        </div>
        </van-pull-refresh>
      </template>

      <!-- ============================================================
           【P239 修复】会议室订单 / 增值订单 两个 Tab 的模板被误删
             （P237 重写消息页时，替换区间从消息页一直吃到采购单之前，
              把 tabIndex===1 / ===2 两段一起删掉了 ⇒ 底部第 2、3 项点不动、列表空白）
             组件 import / components 注册都还在，只是模板没了 ⇒ 这里按「内联 Tab」口径补回。
             ⚠️ 判据用 tabIndex（不是 P183 备份里的 subKey）—— P224 起所有 Tab 都是内联页。
           ============================================================ -->
      <bill-list
        v-if="!subKey && tabIndex === 1"
        ref="billList"
        :key="'room-' + subSeq"
        bill-type="room"
        scope="all"
        api-prefix="operation"
        :ops-mode="true"
        :mask-amount="true"
        :readonly="true"
        :allow-edit="false"
        :show-back="false"
        title="会议室订单"
        @back="closeSub"
        @pushtodo="onPushTodo"
        @finish="onFinish"
      />
      <bill-list
        v-if="!subKey && tabIndex === 2"
        ref="billList"
        :key="'value-' + subSeq"
        bill-type="value"
        scope="all"
        api-prefix="operation"
        :mask-amount="true"
        :readonly="true"
        :allow-edit="false"
        :show-back="false"
        title="增值订单"
        @back="closeSub"
      />

      <!-- ============================================================
           C. Tab 页：消息中心
              【P224】由「二级子页」改为「内联 Tab 页」（Tab4）—— 与房态/会议室/增值/采购一致：
                ① 底部 Tab 栏不再被 v-if 隐藏，用户可随时切走
                ② 去掉顶部返回箭头（Tab 栏本身就是出口）
                ③ 快捷入口点「消息中心」→ openSub('message') 改为切到本 Tab
           ============================================================ -->
      <!-- 【P235 修正】消息中心改回「subKey 子页」判据。
           原先 P233 把它写成 tabIndex===4，但 TABS 只有 5 项（0~4，4 是「快捷入口」），
           两页判据撞车 ⇒ 底部 Tab 第 5 项点不动、进消息后快捷入口再也回不去。
           现口径：subKey==='message' 渲染消息页；底部 Tab 栏在消息页**继续显示**（已去掉 !subKey 条件），
           顶部加返回箭头，用户可随时点 Tab 或箭头离开。 -->
      <!-- 【P237 UI 重设计】消息中心
           · 分类从「全部/待办/抄送」改为「待执行 / 抄送」两类（用户工单 1-2）
           · 待执行区：全选 + 批量「确认执行」；已确认的消息**不再出现**（后端按 service_status 过滤）
           · 视觉：去掉蓝条与多余嵌套，改为「标签 + 时间 + 正文」的单层卡片，信息层级更清晰 -->
      <!-- 【P244】消息中心 → 改用全平台共用组件 MessagePanel
           三 Tab（未读 / 待处理 / 全部）+ 红色气泡角标 + 描述完整展示；
           「已确认」并入「全部」Tab（用户 10-07 确认）。 -->
      <!-- 【P249】按全平台统一组件接口传参：
           · tabs 由本页算好（含角标数），顺序：未读 / 待处理 / 全部
           · 批量：**仅「待处理」Tab 开启**，动作 = confirm（保留批量确认）
           · show-action：卡片底部「去处理」，点击进会议室订单详情 -->
      <message-panel
        v-if="subKey === 'message'"
        title="消息中心"
        :tabs="msgTabsView"
        :tab="msgTab"
        :list="msgRows"
        :loading="msgLoading"
        :refreshing="msgRefreshing"
        :busy="msgConfirming"
        :err="msgErr"
        :batch-enabled="msgTab === 'pending'"
        batch-action="confirm"
        :show-action="true"
        action-text="去处理"
        @back="closeSub"
        @tab="onMsgTab"
        @refresh="loadMessages(true)"
        @readAll="readAll"
        @open="openMessage"
        @action="openMessage"
        @batch="onBatchConfirm"
      />

      <div v-if="!subKey && tabIndex === 3" class="op-sub">
        <div class="customer-header" ref="subHead">
          <div class="customer-header__top">
            <div class="customer-header__left">
              <span class="customer-header__title">采购单</span>
            </div>
            <div class="customer-header__search">
              <el-input v-model="purKw" class="search-input" placeholder="搜索名称/编号" @change="loadPurchase(true)">
                <i slot="prefix" class="el-icon-search"></i>
              </el-input>
            </div>
          </div>
        </div>
        <div class="customer-header-spacer" :style="{ height: headH + 'px' }"></div>
        <div class="bill-sum">
          <span>共 {{ purTotal }} 条</span>
        </div>
        <van-pull-refresh v-model="purRefreshing" @refresh="loadPurchase(true)">
          <div class="list-box">
            <div class="crm-item" v-for="r in purRows" :key="r.sn">
              <div class="item-head customer-card">
                <div class="customer-card__main">
                  <div class="customer-card__content">
                    <div class="customer-card__header">
                      <div class="customer-card__name-wrap">
                        <span class="customer-card__name">{{ r.name || r.finance_no || '采购单' }}</span>
                      </div>
                      <span class="status-tag tag-allot" v-if="r.stage_name">{{ r.stage_name }}</span>
                    </div>
                    <div class="customer-card__row">编号：{{ r.finance_no || '-' }} · 签订：{{ r.sign_date || '-' }}</div>
                    <div class="customer-card__row customer-card__row--muted">创建：{{ r.create_user_name || '-' }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bill-empty" v-if="!purLoading && !purRows.length">
              <div class="bill-empty__icon">📦</div>
              <div class="bill-empty__text">暂无采购单</div>
              <div class="bill-empty__hint">口径：财务合同中 is_purchase=1 的记录（当前数据集中尚无此类单据）</div>
            </div>
          </div>
        </van-pull-refresh>
      </div>

      <!-- ============================================================
           F. Tab 页：房态（【P169】1:1 对齐销售端 client.vue 的房态网格 UI）
              日期区间 header + 门店横滚 + 会议室×日期 四色格子 + 只读详情弹窗
           【P176】由「子页」改为「内联 Tab 页」：
             ① 底部 Tab 栏保持可见（不再因 subKey 被 v-if 隐藏）⇒ 用户可随时切走，解决「出不来」
             ② 进入运营端默认落在此页（tabIndex 默认 0）⇒ 与销售端首屏一致
             ③ 不再需要单独的返回条（Tab 栏本身就是出口），返回条仅保留在 no-tabbar 场景
           ============================================================ -->
      <div v-if="!subKey && tabIndex === 0" class="op-tabpage">
        <div class="room-status-page">
          <div class="room-status-board">

            <div class="header" @click="handleDialogDate">
              <div class="header__date">
                <van-icon name="calendar-o" class="header__cal" />
                <span class="header__range">{{ roomDateRangeValue[0] }} <i>~</i> {{ roomDateRangeValue[1] }}</span>
              </div>
              <div class="header__btn">更换日期 <van-icon name="arrow" /></div>
            </div>

            <!-- 【P181】账号条（P180 新增的「运营人名称 + 退出」）已按需求【整体移除】。
                 房态首屏不再显示姓名/角色/退出入口；退出仍可从「快捷入口」Tab 内触发。
                 历史：P180 因「房态首屏无退出出口」而新增，现用户明确要求去掉。 -->

            <div class="store-wrap">
              <div
                class="store-item"
                :class="{ active: storeSN == item.sn }"
                @click="searchStore(item.sn)"
                v-for="item in storesData"
                :key="item.sn"
              >{{ item.name }}</div>
            </div>

            <!-- 【P180】图例条已按需求【整体移除】。
                 历史：P176 新增（用户当时不知四色含义）；P178 修正过文案。
                 现用户明确要求「图示例部分去掉」⇒ 删除模板 + 对应 CSS + legendOpen 状态。
                 保留四色口径备查：3绿 已成交已收款 / 1蓝 已成交待收款 /
                 2黄 未成交有金额 / 4红 免费预留 / 灰虚线 未使用。 -->

            <div class="scroll-wrapper" :style="roomScrollH ? { height: roomScrollH + 'px' } : ''">
              <div class="scroll-content" :style="'width:' + tableContainerWith + 'px;'">

                <!-- 日期表头 -->
                <div class="date-header">
                  <div
                    class="date-item"
                    :class="{ today: item.isToday }"
                    v-for="item in roomData['dateRange']"
                    :key="item.date"
                  >{{ item.date }}<br>{{ item.weekday }}</div>
                </div>

                <!-- 【P183】★★ 四色口径改为与销售端 client.vue **逐字 1:1 同构**（按用户选定「完全跟随销售端」）：
                     1 蓝 = 有合同(contracts) 且 合同应收(receivables) > 0   —— 已成交待收款
                     2 黄 = 无合同 且 订单金额(amount) > 0                  —— 未成交有金额
                     3 绿 = 有合同 且 合同应收(receivables) <= 0            —— 已成交已收款
                     4 红 = 无合同 且 订单金额(amount) == 0                 —— 免费预留
                     ⚠️ 原 P169/P178 用的 `roomCellTone()`（判定 orders_main + is_payment）语义与此**正交**，
                        实测同一批 64 格：销售端全黄 / 运营端全绿，**0 格一致** ⇒ 已废弃，改直用销售端条件。 -->
                <div class="room-list">
                  <div class="room-row" v-for="(item, idx) in roomData['rooms']" :key="idx">
                    <div class="room-name">{{ item.room_number }}</div>
                    <div class="room-days">
                      <div class="day-cell" v-for="(item2, dIdx) in item['dateRange']" :key="dIdx">
                        <div v-for="(item3, index) in item2['dateArry']" :key="index">

                          <div class="status status--empty" v-if="!item3['order']">
                            <span class="status--empty__txt">未使用</span>
                          </div>

                          <!-- 【P183】1 蓝 = 有合同 且 应收 > 0（销售端 client.vue L69 同条件） -->
                          <div
                            @click="openRoomDetail(item3['order']['sn'])"
                            v-if="item3['order'] && item3['order']['contracts'] && item3['order']['contracts']['receivables'] > 0"
                            class="status content_status_1 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                          <!-- 【P183】2 黄 = 无合同 且 金额 > 0（销售端 client.vue L77 同条件） -->
                          <div
                            @click="openRoomDetail(item3['order']['sn'])"
                            v-if="item3['order'] && (!item3['order']['contracts']) && item3['order']['amount'] > 0"
                            class="status content_status_2 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                          <!-- 【P183】3 绿 = 有合同 且 应收 <= 0（销售端 client.vue L82 同条件） -->
                          <div
                            @click="openRoomDetail(item3['order']['sn'])"
                            v-if="item3['order'] && item3['order']['contracts'] && item3['order']['contracts']['receivables'] <= 0"
                            class="status content_status_3 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                          <!-- 【P183】4 红 = 无合同 且 金额 == 0（销售端 client.vue L88 同条件） -->
                          <div
                            @click="openRoomDetail(item3['order']['sn'])"
                            v-if="item3['order'] && (!item3['order']['contracts']) && item3['order']['amount'] == 0"
                            class="status content_status_4 status_color"
                          >
                            <div class="item_name" :title="item3['order']['customer_name']">{{ item3['order']['customer_name'] }}</div>
                            <div class="item_name">{{ item3['order']['number_people'] }}人</div>
                            <div class="item_name">{{ item3['order']['start_time'] }}</div>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="bill-empty" v-if="!roomLoading && !roomData['rooms']">
                    <div class="bill-empty__icon">🏠</div>
                    <div class="bill-empty__text">该门店暂无会议室</div>
                  </div>
                </div>

              </div>
            </div>

            <!-- 【P170】日期搜索：1:1 移植销售端 client.vue（选择时间对话框 + 日期选择器） -->
            <van-dialog v-model="dialogDatePop" title="选择时间" show-cancel-button @confirm="searchConfirm">
              <div class="dialogDate">
                <div class="dialogDateRow" @click="selectDate(0)"> <span class="dialogDateLable">开始时间</span> <span class="dialogDateValue">{{ beignDate }}</span></div>
                <div class="dialogDateRow" @click="selectDate(1)"> <span class="dialogDateLable">结束时间</span> <span class="dialogDateValue">{{ endDate }}</span></div>
              </div>
            </van-dialog>

            <van-popup v-model="dialogDate" round position="bottom" :style="{ height: '50%' }">
              <van-datetime-picker
                @confirm="dateConfirm"
                @cancel="dateCancel"
                v-model="currentDate"
                type="date"
              />
            </van-popup>

            <!-- ===== 只读详情弹窗（1:1 对齐销售端 detail-popup，去掉「编辑」入口） ===== -->
            <van-popup v-model="dialogDetail" round position="bottom" class="detail-popup" :style="{ height: '82%' }">
              <div class="detail-sheet">
                <div class="detail-sheet__hd">
                  <div class="detail-sheet__title">会议室订单详情</div>
                  <van-icon name="cross" class="detail-sheet__close" @click="closeDetail" />
                </div>

                <div class="detail-sheet__body">
                  <!-- 客户 / 销售 概览 -->
                  <div class="detail-hero">
                    <div class="detail-hero__main">
                      <div class="detail-hero__name">{{ orderDetailData['customers'] || '未知客户' }}</div>
                      <div class="detail-hero__sub">销售负责人：{{ orderDetailData['staff'] || '-' }}</div>
                    </div>
                    <div class="detail-hero__actions">
                      <!-- 【P169-B】运营端金额脱敏：不展示金额，只放服务状态标签 -->
                      <span class="op-svc" :class="'op-svc--' + detailSvc">{{ detailSvcText }}</span>
                    </div>
                  </div>

                  <!-- 常用联系人 -->
                  <div class="detail-sec">
                    <div class="detail-sec__title"><span class="detail-sec__bar"></span>常用联系人</div>
                    <div v-if="orderDetailData['contact'] && orderDetailData['contact'].length" class="detail-cards">
                      <div class="detail-card" v-for="(c, ci) in orderDetailData['contact']" :key="ci">
                        <div class="detail-card__row"><span class="detail-card__label">姓名</span><span class="detail-card__val">{{ c.name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">手机</span><span class="detail-card__val">{{ c.phone }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">微信</span><span class="detail-card__val">{{ c.wx }}</span></div>
                      </div>
                    </div>
                    <div v-else class="detail-empty">暂无联系人</div>
                  </div>

                  <!-- 会议室预定信息 -->
                  <div class="detail-sec">
                    <div class="detail-sec__title"><span class="detail-sec__bar"></span>会议室预定信息</div>
                    <div v-if="orderDetailData['orders_meeting_room'] && orderDetailData['orders_meeting_room'].length" class="detail-cards">
                      <div class="detail-card" v-for="(m, mi) in orderDetailData['orders_meeting_room']" :key="mi">
                        <div class="detail-card__head">{{ m.meeting_room_name }} <em>{{ m.meeting_room_room_number }}</em></div>
                        <div class="detail-card__row"><span class="detail-card__label">类型</span><span class="detail-card__val">{{ m.order_type_name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">桌型</span><span class="detail-card__val">{{ m.table_types }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">设备</span><span class="detail-card__val">{{ m.option_service }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">日期</span><span class="detail-card__val">{{ m.on_site_time }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">时段</span><span class="detail-card__val">{{ m.time_type_name }} · {{ m.start_time }}</span></div>
                        <!-- 【P169-B】金额脱敏：回款计划整组不展示 -->
                      </div>
                    </div>
                    <div v-else class="detail-empty">暂无预定信息</div>
                  </div>

                  <!-- 增值业务 -->
                  <div class="detail-sec">
                    <div class="detail-sec__title"><span class="detail-sec__bar"></span>增值业务</div>
                    <div v-if="orderDetailData['added_services'] && orderDetailData['added_services'].length" class="detail-cards">
                      <div class="detail-card detail-card--sm" v-for="(a, ai) in orderDetailData['added_services']" :key="ai">
                        <div class="detail-card__row"><span class="detail-card__label">时间</span><span class="detail-card__val">{{ a.on_date }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">类型</span><span class="detail-card__val">{{ a.value_added_services_name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">名称</span><span class="detail-card__val">{{ a.value_added_services_goods_name }}</span></div>
                        <div class="detail-card__row"><span class="detail-card__label">数量</span><span class="detail-card__val">{{ a.quantity }}</span></div>
                      </div>
                    </div>
                    <div v-else class="detail-empty">暂无增值业务</div>
                  </div>
                </div>

                <!-- 【P93】运营动作位
                     【P232/P234】★ 去掉「推送待办」，改为「确认(我知道了)」。
                       业务原因：待办由系统自动推送（销售端改单 P230 / PC端改单 P231 /
                       财务审批通过 P229），运营再手动「推送」会造成重复打扰；
                       运营的职责是**确认知悉**，确认后逐级通知上级。
                       阶段语义：0未推送 → 1待确认 → 2已确认 → 3已完成。
                       【P234 修正】原先 svc=0 只显示「待推送(disabled)」，
                       而消息列表最新一条常常就是 svc=0 的变更单 ⇒ 用户看到「没有确认按钮」
                       误以为功能没开发。现改为 **svc 0/1 都可确认**（幂等，后端会判已确认）。 -->
                <div class="op-svc-ft">
                  <van-button
                    v-if="detailSvc === 0 || detailSvc === 1"
                    block type="warning" size="small"
                    :loading="msgConfirming"
                    @click="confirmFromDetail"
                  >确认(我知道了)</van-button>
                  <van-button
                    v-else-if="detailSvc === 2"
                    block plain type="warning" size="small" disabled
                  >已确认</van-button>
                  <van-button
                    v-if="detailSvc < 3"
                    block type="primary" size="small"
                    @click="finishFromDetail"
                  >完成服务</van-button>
                  <van-button v-else block plain size="small" disabled>服务已完成</van-button>
                </div>
              </div>
            </van-popup>

          </div>
        </div>
      </div>
    </template>

    <!-- ===== 底部 Tab（【P235 修正】TABS 实际只有 5 项 → 索引 0~4：
         0房态 1会议室订单 2增值订单 3采购单 4快捷入口；
         消息中心**不再占 Tab 索引**（P233 曾错设 tabIndex===4，与「快捷入口」撞车 ⇒
         底部 Tab 第 5 项点不动、进消息页后「快捷入口」永远显示不出来）。
         消息页改回 subKey 子页 + 底部 Tab 栏继续显示（见下方 v-if）+ 顶部返回箭头，
         这样既不撞索引，用户也随时能点 Tab 切走。 ===== -->
    <van-tabbar v-if="!authDenied" v-model="tabIndex" active-color="#2b5fd9" inactive-color="#969799">
      <van-tabbar-item v-for="(t, i) in tabs" :key="t.key" :icon="t.icon" :badge="t.key === 'quick' && unread.total > 0 ? (unread.total > 99 ? '99+' : String(unread.total)) : ''" @click="onTabClick(i)">{{ t.label }}</van-tabbar-item>
    </van-tabbar>

    <!-- 【P196】退出确认弹窗：复用全站统一 ConfirmDialog（与销售端 client.vue P46 同一套）
         【P181】曾随房态账号条一并移除的旧弹窗已由本次「快捷入口 → 账号 → 退出登录」替代。 -->
    <ConfirmDialog
      v-model="logoutShow"
      title="退出登录"
      desc="确认退出当前账号？退出后需重新登录才能继续使用。"
      confirm-text="退出"
      @confirm="doLogout"
    />
  </div>
</template>

<script>
import {
  operationMyProfile,
  operationDashboard,
  operationRoomStatus,
  operationGetRoomDate,
  operationGetStores,
  operationGetRooms,
  operationPurchaseList,
  operationPurchaseSum,
  operationMessageList,
  operationMessageSummary,
  operationMessageRead,
  operationMessageReadAll,
  operationMessageUnread,
  operationRoomTodoPush,
  operationRoomFinishService,
  /* 【P232】确认待办（我知道了）→ 通知上级 */
  operationRoomTodoConfirm
} from '@/api/api.js'
import { getClientOrderDetailData } from '@/api/api.js'
import { getProfile, logout } from '@/util/clientAuth.js'
import crmWs from '@/util/websocket.js'
import { suppressGlobalError } from '@/util/request.js'
import BillList from '@/components/BillList.vue'
import MessagePanel from '@/components/MessagePanel.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

function svg(inner, size) {
  const n = size || 23
  return '<svg viewBox="0 0 24 24" width="' + n + '" height="' + n + '" fill="none" stroke="currentColor"'
    + ' stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + inner + '</svg>'
}
function money(v) {
  const n = Number(v || 0)
  return '¥' + n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/* 【P176】底部 Tab 定义：Tab0 由「首页」改为「房态」，与销售端 client.vue 首 Tab 一致。
   原首页（画像 + KPI + 快捷九宫格）内容并入「快捷入口」Tab（index 4）。
   【P224】新增 Tab5「消息」：把消息中心从「二级子页」也改为「内联 Tab 页」。
   原因：它是唯一没跟上 Tab 化的入口 —— 仍走 subKey==='message' 子页，
        而底部 Tab 栏是 `v-if="!authDenied && !subKey"` ⇒ 一进消息页 Tab 栏整个消失，
        只能靠顶部返回箭头退出，正是 P176 要消灭的「进得去出不来」。 */
/* 【P231】★ 消息中心从底部 Tab 中去掉。
   原因：消息是「待办提醒」而非主功能区，用户要求只保留快捷入口进入（与销售端一致）。
   ★ 消息页仍保留为内联 Tab（index 4），由 openSub('message') 切到，
     底部 Tab 栏不再显示「消息」这一项 ⇒ TABS 去掉 message 项，
     「快捷入口」index 由 5 变 4，模板中 tabIndex===5 的判据同步改为 ===4。 */
const TABS = [
  { key: 'room_status', label: '房态', icon: 'wap-home-o' },
  { key: 'room_order', label: '会议室订单', icon: 'shop-o' },
  { key: 'value_order', label: '增值订单', icon: 'gift-o' },
  { key: 'purchase', label: '采购单', icon: 'shopping-cart-o' },
  { key: 'quick', label: '快捷入口', icon: 'apps-o' }
]

/* Tab 索引 → 子页 key
   【P176】0 → 房态：★ 必须为 ''，房态作为「内联 Tab 页」渲染（见模板 tabIndex===0），
           不能当子页 —— 子页会因 v-if="!subKey" 把底部 Tab 栏整个隐藏掉，
           用户就会「进得去出不来」，这正是本次要修的问题。
           1/2/3 → 各自子页；4 → ''（快捷入口，渲染并入的原首页内容）
   【P224】★ 全部 6 个 Tab 均为内联页 ⇒ 一律为 ''。
           保留本映射表结构：历史遗留的 openSub/quickItems 仍按 key 切 Tab，
           未来若再引入真子页，只需在此登记即可，不必改模板。 */
const TAB_SUB = { 0: '', 1: 'room_order', 2: 'value_order', 3: 'purchase', 4: '', 5: '' }

const SCENE_TEXT = {
  room_order_todo: '会议室待办',
  room_order_cc: '逐级抄送',
  room_order_finish: '服务完成'
}

export default {
  name: 'OperationHome',
  components: { BillList, MessagePanel, ConfirmDialog },
  data() {
    return {
      authDenied: false,
      authMsg: '',
      profile: {},
      unread: { primary: 0, cc: 0, total: 0 },
      dash: { room: {}, value: {}, purchase: {} },
      tabIndex: 0,
      /* 【P112】首页（含快捷入口）下拉刷新 */
      homeRefreshing: false,
      subKey: '',
      subSeq: 0,
      /** 【P93】当前子页固定头部实测高度 → spacer 占位（否则内容被 position:fixed 头部遮住） */
      headH: 0,
      tabs: TABS,
      /* 消息中心 */
      msgRows: [],
      msgTotal: 0,
      /* 【P240】默认落在「待确认」分类 */
      msgTab: 'pending',
      /* 【P240】四类计数（待确认/已确认/未读/抄送），由 messageSummary 拉取 */
      msgCounts: { unread: 0, pending: 0 },
      msgLoading: false,
      msgRefreshing: false,
      /* 【P232-fix】消息列表加载失败原因（渲染到页面，便于线上自查） */
      msgErr: '',
      /* 【P232】批量确认：勾选中的消息 sn + 确认中态 */
      msgPicked: [],
      msgConfirming: false,
      /* 采购单 */
      purRows: [],
      purTotal: 0,
      purSum: 0,
      purKw: '',
      purLoading: false,
      purRefreshing: false,
      /* 房态（【P169】与销售端 client.vue 同构：日期区间 + 门店横滚 + 会议室×日期 网格） */
      storesData: [],
      storeSN: '',
      roomDateRangeValue: [],
      roomCurDate: {},
      roomData: {},
      tableContainerWith: 0,
      roomLoading: false,
      /* 【P180】房态滚动区动态高度
         （原 P176 的 legendOpen 图例折叠态已随图例条整体移除）
         【P181】原 P180 的 logoutShow（账号条退出弹窗）已随账号条一并移除
         【P196】logoutShow 恢复：退出入口迁到「快捷入口 → 账号」，弹窗复用 ConfirmDialog */
      logoutShow: false,
      roomScrollH: '',
      /* 【P170】日期搜索：与销售端 client.vue 同构（原运营端缺失 → 「更换日期」不可用） */
      dialogDate: false,
      dialogDatePop: false,
      dialogDateType: 0,
      currentDate: new Date(),
      beignDate: '',
      endDate: '',
      /* 只读详情弹窗 */
      dialogDetail: false,
      orderDetailData: {},
      currentDetailSn: '',
      detailSvc: 0,
      /* 【P234】从消息进详情时记录来源 Tab（4=消息 / 5=快捷入口），
         关闭详情弹层后退回该页，避免「点条消息就跳到房态页、回来还得自己找路」。 */
      msgReturnTab: 4,
      quickItems: [
        { key: 'message',    label: '消息中心',   icon: svg('<path d="M4 19V5l8 4 8-4v14"/><path d="M4 19h16"/>') },
        { key: 'room_status',label: '房态',       icon: svg('<path d="M4 20V10l8-6 8 6v10H4z"/><path d="M10 20v-5h4v5"/>') },
        { key: 'room_order', label: '会议室订单', icon: svg('<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 11h18"/>') },
        { key: 'value_order',label: '增值订单',   icon: svg('<rect x="4" y="10" width="16" height="10" rx="1.5"/><path d="M4 10 6 4h12l2 6"/><path d="M12 4v6"/>') },
        { key: 'purchase',   label: '采购单',     icon: svg('<circle cx="9.5" cy="19.5" r="1.4"/><circle cx="17" cy="19.5" r="1.4"/><path d="M3 4h2.5l2.4 10.5h10.4L21 7H6"/>') },
        { key: 'stat',       label: '统计',       icon: svg('<path d="M5 19V9"/><path d="M12 19V5"/><path d="M19 19v-7"/>') }
      ]
    }
  },
  computed: {
    /* 【P249】三 Tab 定义（顺序：未读 / 待处理 / 全部），角标取自 msgCounts */
    msgTabsView() {
      return [
        { value: 'unread',  label: '未读',   count: Number(this.msgCounts.unread)  || 0 },
        { value: 'pending', label: '待处理', count: Number(this.msgCounts.pending) || 0 },
        { value: 'all',     label: '全部',   count: 0 },
      ]
    },
    avatarText() {
      const n = this.profile.name || '运营'
      return String(n).slice(0, 1)
    },
    deptText() {
      return (this.profile.dept_names || []).join(' / ')
    },
    roleText() {
      return (this.profile.role_names || []).join(' / ')
    },
    purSumText() {
      return this.purSum > 0 ? ('合计 ' + money(this.purSum)) : ''
    },
    /** 【P169】房态详情：服务状态文案 */
    detailSvcText() {
      const t = { 0: '待推送', 1: '待店长确认', 2: '店长已确认', 3: '服务已完成' }
      return t[this.detailSvc] || '待推送'
    },
    /* 【P236-fix】批量「全选」复选框状态
       ★★ 必须放在 computed，不能放 methods：
         放 methods 时 Vue 会当普通方法注册，而模板里 :checked="msgPickedAll" 却按 getter 求值
         ⇒ 组件初始化早期（data 未就绪）访问 msgRows.length 直接抛
         「Cannot read properties of undefined (reading 'length')」→ 运营端白屏进不去。
       ★ 仍加数组兜底，避免异常路径下再次炸掉整页。 */
    msgPickedAll() {
      const rows = this.msgRows || []
      const picked = this.msgPicked || []
      return rows.length > 0 && picked.length === rows.length
    }
  },
  watch: {
    tabIndex(v) {
      const sub = TAB_SUB[v]
      if (sub !== undefined) {
        if (sub === '') { this.subKey = '' } else { this.openSub(sub) }
      }
      /* 【P232-fix】★ 消息中心是内联 Tab（index 4），TAB_SUB[4]==='' ⇒ 上面这行不会走 openSub。
         切到消息页时**必须在这里直接拉数据** ——
         之前只在 openSub('message') 里拉，而「点九宫格」这条路径在某些时序下
         tabIndex 已经是 4（v-if 不重渲染），loadMessages 不会被调用 ⇒ 列表空白。 */
      if (v === 4) { this.loadMessages(true) }
      /* 【P176】切到房态 Tab（0）时确保数据已加载 + 重算滚动区高度
         仅在「尚未拉过门店列表」时补拉，避免每次切 Tab 都重复请求 */
      if (v === 0) {
        if (!this.storesData.length) { this.loadRoomStatus() }
        this.$nextTick(this.measureRoomScrollH)
      }
    }
  },
  created() {
    this.loadProfile()
    this.loadDashboard()
    this.loadUnread()
    /* 【P176】默认落在房态 Tab（tabIndex=0），watch 只响应「变化」⇒ 首屏需主动拉一次 */
    this.loadRoomStatus()
  },
  mounted() {
    /* 【P176】窗口尺寸变化时重算房态滚动区高度（房态现为内联 Tab，判据改为 tabIndex） */
    this._onWinResize = () => { if (!this.subKey && this.tabIndex === 0) this.measureRoomScrollH() }
    window.addEventListener('resize', this._onWinResize)
  },
  beforeDestroy() {
    if (this._onWinResize) { window.removeEventListener('resize', this._onWinResize) }
  },
  methods: {
    money,

    /* ---------------- 身份 ---------------- */
    loadProfile() {
      const cached = getProfile() || {}
      this.profile = Object.assign({}, cached)
      operationMyProfile({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.profile = Object.assign({}, cached, res.data)
          this.authDenied = false
        } else if (res && res.code === 403) {
          this.authDenied = true
          this.authMsg = res.msg || '无运营人员权限'
        }
      }).catch(err => {
        const msg = (err && err.msg) || ''
        if (String(msg).indexOf('运营') !== -1) {
          this.authDenied = true
          this.authMsg = msg
        }
      })
    },
    loadDashboard() {
      /* 【P112】返回 Promise，供首页下拉刷新收尾 */
      return operationDashboard({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.dash = res.data
          if (res.data.unread) { this.unread = res.data.unread }
        }
      }).catch(() => {})
    },
    loadUnread() {
      console.log('[MSG_FLOW] FE loadUnread.enter')
      operationMessageUnread({}).then(res => {
        if (res && res.code === 200 && res.data) { this.unread = res.data }
        console.log('[MSG_FLOW] FE loadUnread.done', { code: res && res.code, data: res && res.data })
      }).catch(() => {})
    },
    /* 【P112】首页（含快捷入口）下拉刷新
       刷新对象 = KPI 看板（loadDashboard，含 5 个 Tab 的计数）+ 未读角标（loadUnread）。
       与 refreshCurrent() 同源，但那个是「子页」用的（会顺手刷 BillList），首页不需要。 */
    onHomeRefresh() {
      const done = () => { this.homeRefreshing = false }
      try {
        const p = this.loadDashboard()
        this.loadUnread()
        if (p && typeof p.then === 'function') { p.then(done, done) }
        else { setTimeout(done, 800) }
      } catch (e) { done() }
    },
    onLogout() {
      /* 【P118】logout() 内部已按「当前端」跳到本端登录页（运营 → #/operation），
         这里不要再二次硬编码跳 #/login，否则会覆盖掉正确落点 */
      logout()
    },

    /* 【P196】退出登录（与销售端 client.vue P46 同一交互）：
       点「账号 → 退出登录」先弹全站统一的 ConfirmDialog 二次确认，再执行登出。 */
    askLogout() {
      this.logoutShow = true
    },
    doLogout() {
      this.logoutShow = false
      /* 关掉 WS 推送，再清本地鉴权并回本端登录页（与销售端 doLogout 同一实现） */
      try { crmWs.close() } catch (e) { /* ignore */ }
      this.onLogout()
    },

    /* ---------------- 子页导航 ---------------- */
    openSub(key) {
      /* 【P176】房态已改为内联 Tab（非子页）⇒ 从快捷入口点进来时切 Tab 而非开子页，
         否则会因 subKey 非空把底部 Tab 栏隐藏，又变回「出不来」的老问题 */
      if (key === 'room_status') {
        this.subKey = ''
        this.tabIndex = 0
        if (!this.storesData.length) { this.loadRoomStatus() }
        this.$nextTick(this.measureRoomScrollH)
        return
      }
      if (key === 'stat') { this.$toast('统计模块待接入'); return }
      /* 【P235 修正】★ 消息中心**回到 subKey 子页**。
         走内联 Tab（tabIndex===4）会与「快捷入口」撞车 ——
         TABS 只有 5 项（0~4，4 就是快捷入口），两页判据不可能都是 4。
         底部 Tab 栏已改为「消息页也显示」（去掉了 !subKey 条件）+ 顶部加了返回箭头，
         所以既不会「进得去出不来」，也不会点不动 Tab。 */
      if (key === 'message') {
        this.subKey = 'message'
        this.subSeq++
        this.loadMessages(true)
        this.loadMsgSummary()
        this.$nextTick(this.measureHead)
        return
      }
      /* 【P224】★ 全部 Tab 均为内联页 ⇒ 统一「按 key 切到目标 Tab」，绝不再设 subKey。
         过去这里对 message 走 `this.subKey = key` 开二级子页，导致：
           ① 底部 Tab 栏（v-if="!authDenied && !subKey"）在消息页整条消失；
           ② 用户只能靠顶部返回箭头退出 = P176 明令要消灭的「进得去出不来」。
         改法：反查 TAB_SUB 拿到索引 → 切 tabIndex。
         subSeq 仍自增，强制 BillList 等列表组件重挂载重拉数据（:key="'room-' + subSeq"）。 */
      const target = Object.keys(TAB_SUB).find(i => TAB_SUB[i] === key)

      if (target !== undefined) {
        this.subKey = ''
        this.tabIndex = Number(target)
        this.subSeq++
        if (key === 'message') { this.loadMessages(true) }
        if (key === 'purchase') { this.loadPurchase(true) }
        this.$nextTick(this.measureHead)
        return
      }
      /* 兜底：未在 TAB_SUB 登记的 key 才退化为子页（保留扩展位） */
      this.subKey = key
      this.subSeq++
      this.loadMessages(true)
      this.measureHead()
    },
    /** 【P93】量出当前子页固定头部真实高度（三处 header 共用 ref="subHead"，v-if 同时只渲染一个） */
    measureHead() {
      this.$nextTick(() => {
        const el = this.$refs.subHead
        const h = el ? el.offsetHeight : 0
        if (h && h !== this.headH) { this.headH = h }
      })
    },
    closeSub() {
      /* 【P235】关闭子页时**回到来源页**：
         从快捷入口(4)进消息 → 关掉回快捷入口；其它子页回房态(0)。
         避免「关掉消息后落在房态页」让用户找不到北。 */
      const back = (this.msgReturnTab === 4) ? 4 : 0
      this.subKey = ''
      this.tabIndex = back
      if (back === 4) { this.$nextTick(() => { this.loadMessages(true) }) }
    },
    /** 【P235】底部 Tab 点击：任何子页下点 Tab 都要先清 subKey，否则页面不换 */
    onTabClick(i) {
      if (this.subKey) {
        this.subKey = ''
        this.msgReturnTab = i
      }
      this.tabIndex = i
    },

    /* ---------------- 消息中心（P244 三 Tab：未读 / 待处理 / 全部） ---------------- */
    /** 切 Tab。unread=未读(阅读态) · pending=待处理(is_action=0) · all=全部 */
    onMsgTab(k) {
      this.msgTab = k
      this.msgPicked = []
      this.loadMessages(true)
    },
    /** 【P244】批量确认：把勾选中的消息对应订单一次性确认 */
    onBatchConfirm(payload) {
      const sns = (payload && payload.sns) ? payload.sns : payload
      if (!Array.isArray(sns) || !sns.length) { this.$toast('请先勾选要确认的消息'); return }
      this.doConfirm(sns, '已确认并通知上级')
    },
    /** 【P244】拉角标计数（只取「未读」「待处理」两个） */
    loadMsgSummary() {
      operationMessageSummary({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.msgCounts = {
            unread:  Number(res.data.unread)  || 0,
            pending: Number(res.data.pending) || 0,
          }
        }
      }).catch(() => { /* 角标失败不打扰用户 */ })
    },
    loadMessages(reset) {
      if (reset) { this.msgRows = [] }
      this.msgLoading = true
      this.msgErr = ''
      const body = { page: 1, pageSize: 50 }
      /* 【P244】三 Tab → 后端筛选参数：
       *   pending 待处理 → confirm=0（role=1 主送 + is_action=0）
       *   unread  未读   → is_read=0（阅读态，跨业务态，不限 role）
       *   all     全部   → 不传筛选，后端返回该账号全部消息（含已确认/抄送）
       * ★ 「已确认」不再单独占 Tab，已并入「全部」（用户 10-07 确认）。 */
      if (this.msgTab === 'pending') { body.confirm = 0 }
      if (this.msgTab === 'unread')  { body.is_read = 0 }
      operationMessageList(body).then(res => {
        /* 【P232-fix】把「接口实际返回了什么」显式落到页面上 ——
           原先只 console.log，线上列表空时完全无从判断是 code 不对、还是 data 结构不符。
           msgErr 现在承载「诊断信息」，成功时自动清空。 */
        if (!res) { this.msgErr = '无响应（res 为空）'; return }
        if (res.code !== 200) {
          this.msgErr = '接口返回 code=' + res.code + ' msg=' + (res.msg || '-')
            + (res.code === 452 ? '（token 失效，请重新登录）' : '')
            + (res.code === 451 ? '（缺少 token）' : '')
          return
        }
        const d = res.data
        const list = (d && Array.isArray(d.data)) ? d.data : []
        if (!list.length) {
          this.msgErr = '接口返回 0 条（total=' + (d && d.total != null ? d.total : '?') + '）'
        } else {
          this.msgErr = ''
        }
        let rows = list
        if (this.msgTab === 'pending') {
          /* 待处理：主送 + 未确认（后端已过滤，这里双保险） */
          rows = list.filter(x => Number(x.role) === 1 && Number(x.is_action) === 0)
        } else if (this.msgTab === 'unread') {
          /* 未读：阅读态，跨业务态 */
          rows = list.filter(x => Number(x.is_read) === 0)
        }
        this.msgRows = reset ? rows : this.msgRows.concat(rows)
        this.msgTotal = d ? (d.total || this.msgRows.length) : this.msgRows.length
      }).catch(e => {
        this.msgErr = '请求异常：' + ((e && e.message) || '未知错误')
      }).then(() => {
        this.msgLoading = false
        this.msgRefreshing = false
      })
    },
    openMessage(m) {
      console.log('[MSG_FLOW] FE openMessage.enter', { sn: m.sn, is_read: m.is_read })
      if (!m.is_read) {
        operationMessageRead({ sn: m.sn }).then(res => {
          console.log('[MSG_FLOW] FE openMessage.read', { sn: m.sn, code: res && res.code, unread: res && res.data })
          if (res && res.code === 200) {
            m.is_read = 1
            if (res.data) { this.unread = res.data }
          }
        }).catch(() => {})
      }
      /* 【P232/P234-fix】★ 会议室订单类消息 → **同页打开该订单的详情弹层**。
         · 原先只 openSub('room_order') 跳订单列表，用户还得自己再找那一单 ⇒ 实际等于「点了没反应」；
         · 但如果为了开弹层把 tabIndex 切到 0（房态，弹层的宿主容器），
           用户关闭弹层后会**落在房态页**，等于「莫名其妙跳走」。
         折中方案：**记录来源页**，弹层关闭后回消息页；
         且优先尝试「不改 Tab 直接开弹层」（当 message 页 DOM 里恰好已有弹层宿主时可用）。 */
      if ((m.page_api || '').indexOf('operation_room') !== -1) {
        const orderSn = this.orderSnOf(m)
        if (orderSn) {
          /* 记住来源页，关闭详情后要退回来（【P235】） */
          this.msgReturnTab = 4
          this.openRoomDetail(orderSn)
        } else {
          this.closeSub()
          this.$nextTick(() => { this.openSub('room_order') })
        }
      }
    },

    /* ================= 【P232】批量/单条「确认（我知道了）」 ================= */
    isPicked(m) {
      return (this.msgPicked || []).indexOf(m.sn) >= 0;
    },
    /* 【P236-fix】msgPickedAll 已迁到 computed（见上方 computed 区块）——
       放在 methods 里会被 Vue 当普通方法，而模板按 getter 求值，导致初始化期崩溃。 */
    togglePick(m) {
      if (!this.msgPicked) { this.msgPicked = [] }
      const i = this.msgPicked.indexOf(m.sn);
      if (i >= 0) { this.msgPicked.splice(i, 1) } else { this.msgPicked.push(m.sn) }
    },
    togglePickAll(e) {
      if (e && e.target && e.target.checked) {
        this.msgPicked = (this.msgRows || []).map(m => m.sn);
      } else {
        this.msgPicked = [];
      }
    },
    /** 把一条消息对应到会议室订单 sn（消息的 parent_sn 就是订单 sn） */
    orderSnOf(m) {
      return (m && (m.parent_sn || m.page_sn)) || '';
    },
    confirmPicked() {
      /* 【P236-fix】数组兜底：与 msgPickedAll 同类问题，防止异常路径再炸整页 */
      const sns = (this.msgPicked || [])
        .map(sn => {
          const m = (this.msgRows || []).find(x => x.sn === sn);
          return m ? this.orderSnOf(m) : '';
        })
        .filter(x => x);
      if (!sns.length) { this.$toast('所选消息没有关联会议室订单'); return }
      this.doConfirm(sns, '已确认 ' + sns.length + ' 条');
    },
    /** 订单详情内的单条确认 */
    confirmOrder(sn) {
      if (!sn) { this.$toast('缺少订单 sn'); return }
      this.doConfirm([sn], '已确认');
    },
    doConfirm(sns, okTip) {
      if (this.msgConfirming) return;
      this.msgConfirming = true;
      console.log('[MSG_FLOW] FE confirm.enter', { sns: sns });
      operationRoomTodoConfirm({ sns: sns }).then(res => {
        this.msgConfirming = false;
        console.log('[MSG_FLOW] FE confirm.done', { code: res && res.code, data: res && res.data });
        if (res && res.code === 200) {
          this.$toast(res.msg || okTip);
          this.msgPicked = [];
          this.loadUnread();
          this.loadMessages(true);
          /* 【P240】确认后「待确认」减、「已确认」增 ⇒ 必须刷新角标 */
          this.loadMsgSummary();
          /* 会议室订单列表也要刷新：service_status 已推进（确认态） */
          this.subSeq++;
        } else {
          this.$toast((res && res.msg) || '确认失败', 'fail');
        }
      }).catch(() => { this.msgConfirming = false; });
    },
    readAll() {
      console.log('[MSG_FLOW] FE readAll.enter')
      operationMessageReadAll({}).then(res => {
        console.log('[MSG_FLOW] FE readAll.done', { code: res && res.code, unread: res && res.data && res.data.unread })
        if (res && res.code === 200) {
          this.$toast('已全部标为已读')
          this.msgRows = this.msgRows.map(x => Object.assign({}, x, { is_read: 1 }))
          if (res.data && res.data.unread) { this.unread = res.data.unread }
        }
      }).catch(() => {})
    },

    /* ---------------- 运营动作：推送待办 / 完成服务 ---------------- */
    onPushTodo(item) {
      if (!item || !item.sn) { return }
      this.$dialog.confirm({
        title: '推送待办',
        message: '将向门店店长推送待办，并逐级抄送上级负责人。是否继续？'
      }).then(() => {
        operationRoomTodoPush({ sn: item.sn }).then(res => {
          const d = res && res.data
          console.log('[MSG_FLOW] FE onPushTodo.done', { sn: item.sn, code: res && res.code, ok: d && d.ok, skip: d && d.skip, details: d && d.details })
          if (res && res.code === 200 && d) {
            suppressGlobalError()
            const ok = d.ok || 0
            const skip = d.skip || 0
            const det = (d.details || [])[0] || {}
            if (ok > 0) {
              this.$toast('推送成功')
            } else if (skip > 0) {
              this.$toast(det.msg || '已推送过，跳过')
            } else {
              this.$toast(det.msg || '推送失败')
            }
            this.refreshCurrent()
          } else {
            this.$toast((res && res.msg) || '推送失败')
          }
        }).catch(() => {})
      }).catch(() => {})
    },
    onFinish(item) {
      if (!item || !item.sn) { return }
      this.$dialog.confirm({
        title: '完成服务',
        message: '确认该会议室订单服务已完成？\n将同步把销售合同标记为「已完成」。'
      }).then(() => {
        operationRoomFinishService({ sn: item.sn, remark: '' }).then(res => {
          const d = res && res.data
          console.log('[MSG_FLOW] FE onFinish.done', { sn: item.sn, code: res && res.code, ok: d && d.ok })
          if (res && res.code === 200 && d && d.ok) {
            suppressGlobalError()
            this.$toast('服务已完成，销售合同已同步')
            this.refreshCurrent()
          } else {
            this.$toast((res && res.msg) || '操作失败')
          }
        }).catch(() => {})
      }).catch(() => {})
    },
    refreshCurrent() {
      const bl = this.$refs.billList
      if (bl && typeof bl.onRefresh === 'function') { bl.onRefresh() }
      this.loadDashboard()
      this.loadUnread()
    },

    /* ---------------- 采购单 ---------------- */
    loadPurchase(reset) {
      if (reset) { this.purRows = [] }
      this.purLoading = true
      operationPurchaseList({ page: 1, pageSize: 30, searchText: this.purKw }).then(res => {
        const d = (res && res.code === 200) ? res.data : null
        const list = d && Array.isArray(d.data) ? d.data : []
        this.purRows = reset ? list : this.purRows.concat(list)
        this.purTotal = d ? d.total : this.purRows.length
      }).catch(() => {}).then(() => {
        this.purLoading = false
        this.purRefreshing = false
      })
      operationPurchaseSum({ searchText: this.purKw }).then(res => {
        if (res && res.code === 200 && res.data) { this.purSum = Number(res.data.amount || 0) }
      }).catch(() => {})
    },

    /* ---------------- 房态（【P169】与销售端 client.vue 1:1 同构） ---------------- */

    /** 入口：拉取本周日期区间 → 门店列表 → 首个门店房态网格 */
    loadRoomStatus() {
      this.roomLoading = true
      operationGetRoomDate({}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.roomCurDate = res.data
          this.roomDateRangeValue = res.data['weekRangeValue'] || []
          this.getStoresData()
        } else {
          this.roomLoading = false
        }
      }).catch(() => { this.roomLoading = false })
    },
    getStoresData() {
      operationGetStores({}).then(res => {
        if (res && res.code === 200) {
          this.storesData = res.data || []
          if (this.storesData.length > 0) {
            this.storeSN = this.storesData[0].sn
            this.getRoomsData()
          } else {
            this.roomLoading = false
          }
        } else {
          this.roomLoading = false
        }
      }).catch(() => { this.roomLoading = false })
    },
    getRoomsData() {
      if (!Array.isArray(this.roomDateRangeValue)) { this.loadRoomStatus(); return }
      this.roomLoading = true
      operationGetRooms({
        option: 0,
        storeSN: this.storeSN,
        roomDateRangeValue: this.roomDateRangeValue
      }).then(res => {
        if (res && res.code === 200) {
          this.roomData = res.data || {}
          const dateRange = this.roomData['dateRange'] || []
          /* 【P180】列宽 78 → 84，与 CSS 中 .date-item/.day-cell 的 84px 保持一致
             （对齐销售端）。⚠️ 此值与 CSS 必须同步改，否则 scroll-content 宽度
             与实际列宽不符 ⇒ 横向滚动尽头会多出/少掉一截空白。 */
          this.tableContainerWith = dateRange.length * 84
          this.$nextTick(this.measureRoomScrollH)
        }
      }).catch(() => {}).then(() => { this.roomLoading = false })
    },
    /**
     * 【P176】动态计算房态滚动区高度：
     * 此前写死 calc(100vh - 168px)，未计入「无底部 Tab 栏 / 图例折叠」等场景，实测底部空出 53px。
     * 改为量测「滚动区顶部距视口顶部」后用剩余高度填满。
     * ★ 房态改为内联 Tab 页后底部 Tab 栏常驻 ⇒ 必须再减掉 Tab 栏高度，否则最后一行被压住。
     */
    measureRoomScrollH() {
      const el = this.$el && this.$el.querySelector ? this.$el.querySelector('.room-status-page .scroll-wrapper') : null
      if (!el) { this.roomScrollH = ''; return }
      const top = el.getBoundingClientRect().top
      const bar = this.$el.querySelector ? this.$el.querySelector('.van-tabbar') : null
      const barH = bar ? bar.getBoundingClientRect().height : 0
      const h = Math.max(240, Math.floor(window.innerHeight - top - barH))
      this.roomScrollH = h
    },
    searchStore(sn) {
      this.storeSN = sn
      this.getRoomsData()
    },
    /* 【P170】日期搜索（1:1 移植销售端 client.vue）：
       点 header → 打开「选择时间」对话框 → 点开始/结束时间 → 底部日期选择器 → 确定 → searchConfirm 重拉网格 */
    handleDialogDate() {
      if (!this.beignDate) {
        this.beignDate = (this.roomDateRangeValue && this.roomDateRangeValue[0]) || ''
        this.endDate = (this.roomDateRangeValue && this.roomDateRangeValue[1]) || ''
      }
      this.dialogDatePop = true
    },
    selectDate(type) {
      this.dialogDateType = type
      const cur = type === 0 ? this.beignDate : this.endDate
      this.currentDate = cur ? new Date(String(cur).replace(/-/g, '/')) : new Date()
      this.dialogDate = true
    },
    dateConfirm(value) {
      this.dialogDate = false
      const date = new Date(value || this.currentDate)
      const y = date.getFullYear()
      const m = date.getMonth() + 1
      const d = date.getDate()
      const formattedDate = y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0')
      if (this.dialogDateType === 0) { this.beignDate = formattedDate }
      if (this.dialogDateType === 1) { this.endDate = formattedDate }
    },
    dateCancel() {
      this.dialogDate = false
    },
    searchConfirm() {
      if (!this.beignDate || !this.endDate) { this.$toast('请选择起止日期'); return }
      if (String(this.beignDate) > String(this.endDate)) { this.$toast('开始时间不能晚于结束时间'); return }
      this.roomDateRangeValue = [this.beignDate, this.endDate]
      this.getRoomsData()
    },
    /**
     * 【P183】★★ `roomCellTone()` 已【整体删除】。
     * 原实现（P169 引入 / P178 修注释）判定口径为：
     *   hasContract = !!order.orders_main || !!order.contracts_sn
     *   paid        = Number(order.is_payment) === 1
     *   ⇒ hasContract ? (paid ? 3 绿 : 1 蓝) : (amt > 0 ? 2 黄 : 4 红)
     * 与销售端 client.vue 的四色条件（依赖 contracts.receivables / amount）**语义正交**：
     *   · orders_main 来自 parent_sn→t_pro_orders，实测覆盖率 4566/4656 ≈ 98% ⇒ 恒 truthy ⇒ 永落 3/1
     *   · contracts 来自 contracts_sn→t_pro_contracts，全库填充率仅 1113/4656 ≈ 24% ⇒ 销售端常落 2/4
     * 实测同一批 64 格两端 **0 格一致**（销售端全黄 / 运营端全绿）。
     * 按用户选定「完全跟随销售端」，改由模板内 4 个显式 v-if 直判，与 client.vue 逐字同构。
     */
    /** 【P169】只读详情弹窗（销售端 handlDetail 同源，去掉编辑/合同入口） */
    openRoomDetail(sn) {
      if (!sn) return
      this.currentDetailSn = sn
      this.orderDetailData = {}
      this.detailSvc = 0
      /* 【P232-fix】★ 详情弹窗（dialogDetail）实际渲染在**房态 Tab**（tabIndex===0）的
         v-if 容器内（index.vue:267-499），而消息页是 tabIndex===4 ⇒ 直接开弹层，DOM 不会生成。
         所以：**先切到房态 Tab，等 DOM 就绪后再开弹层**。
         （实测过切到 Tab1「会议室订单」也不行 —— 那里只有 bill-list，不含该弹窗。）
         【P234】msgReturnTab 记录来源页（消息=4 / 快捷=5），
         关闭弹层后由 closeDetail() 自动退回，避免「点条消息就莫名跳到房态页」。 */
      const openAfterTab = () => {
        this.dialogDetail = true
        getClientOrderDetailData({ sn: sn }).then(res => {
          if (res && res.code === 200) {
            this.orderDetailData = res.data || {}
          }
        }).catch(() => {})
        /* 服务状态：走运营端自己的详情接口 */
        this.syncDetailSvc(sn);
      }
      if (this.tabIndex !== 0) {
        this.subKey = ''
        this.tabIndex = 0
        this.subSeq++
        this.$nextTick(() => { this.$nextTick(openAfterTab) })
        return
      }
      openAfterTab()
    },
    /* 【P232】拉取并设置详情页的服务状态（打开详情与确认后复用） */
    syncDetailSvc(sn) {
      operationRoomOrderDetail({ sn: sn }).then(res => {
        if (res && res.code === 200 && res.data && res.data.room) {
          this.detailSvc = Number(res.data.room.service_status || 0)
        }
      }).catch(() => {})
    },
    pushTodoFromDetail() {
      this.onPushTodo({ sn: this.currentDetailSn })
    },
    /* 【P232】详情内「确认(我知道了)」→ 通知上级；确认后刷新详情状态显示为已确认 */
    confirmFromDetail() {
      const sn = this.currentDetailSn;
      if (!sn) { this.$toast('缺少订单 sn'); return }
      this.doConfirm([sn], '已确认并通知上级');
      const t = setTimeout(() => { this.syncDetailSvc(sn); clearTimeout(t) }, 900);
    },
    /* 【P234/P235】关闭详情弹层：退回消息页，而不是停在房态页 */
    closeDetail() {
      this.dialogDetail = false
      const back = this.msgReturnTab
      this.$nextTick(() => {
        this.subKey = ''
        /* 消息页现在是 subKey 子页 ⇒ 必须回到 subKey 态 */
        this.tabIndex = (back === 5) ? 4 : back
        if (back === 4) {
          this.subKey = 'message'
          this.loadMessages(true)
        }
      })
    },
    finishFromDetail() {
      this.onFinish({ sn: this.currentDetailSn })
    }
  }
}
</script>

<style scoped>
.op-page { min-height: 100vh; background: var(--page-bg, #f5f6f8); padding-bottom: 60px; }
.op-topback { margin-right: 6px; font-size: 18px; color: #323233; }

/* 权限不足 */
.op-denied { padding: 60px 20px 20px; text-align: center; }
.op-denied__ic { font-size: 52px; color: #f0a020; margin-bottom: 12px; }
.op-denied__t { font-size: 18px; font-weight: 700; color: #1d2129; margin-bottom: 8px; }
.op-denied__m { font-size: 13px; color: #8c8f94; margin-bottom: 18px; }
.op-denied__card { background: #fff; border-radius: 16px; padding: 14px 16px; margin-bottom: 14px; text-align: left; box-shadow: 0 2px 12px rgba(20,40,80,.06); }
.op-denied__row { display: flex; justify-content: space-between; font-size: 13px; color: #646566; padding: 6px 0; }
.op-denied__row b { color: #1d2129; font-weight: 600; }
.op-denied__hint { font-size: 12px; color: #969799; line-height: 1.7; margin-bottom: 20px; text-align: left; }

/* Hero */
/* 【P114】与财务端 .fn2-hero 同一套规格（渐变 / 头像 / 字号） */
.op-hero { background: linear-gradient(135deg, #2b5fd9 0%, #4a86f0 100%); padding: 20px 16px 18px; color: #fff; border-radius: 0 0 20px 20px; }
.op-hero__top { display: flex; align-items: center; gap: 12px; }
.op-avatar { width: 44px; height: 44px; border-radius: 50%; background: rgba(255,255,255,.22); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 600; flex: 0 0 44px; }
.op-hero__id { flex: 1; min-width: 0; }
.op-hero__name { font-size: 17px; font-weight: 600; }
.op-hero__dept { font-size: 12px; opacity: .82; margin-top: 3px; }
.op-hero__role { font-size: 12px; background: rgba(255,255,255,.22); border-radius: 12px; padding: 3px 10px; flex: 0 0 auto; }
.op-kpis { display: flex; gap: 10px; margin-top: 16px; }
.op-kpi { flex: 1; background: rgba(255,255,255,.16); border-radius: 14px; padding: 12px 10px; text-align: center; position: relative; }
.op-kpi__n { font-size: 20px; font-weight: 700; }
.op-kpi__dot { position: absolute; top: 10px; right: 14px; width: 7px; height: 7px; border-radius: 50%; background: #ff4d4f; }
.op-kpi__l { font-size: 11.5px; opacity: .88; margin-top: 3px; }

/* 快捷入口 */
.op-quick { background: #fff; border-radius: 16px; margin: 12px; padding: 14px 14px 8px; box-shadow: 0 2px 12px rgba(20,40,80,.06); }
.op-quick__hd { font-size: 15px; font-weight: 700; color: #1d2129; display: flex; align-items: center; gap: 7px; padding-bottom: 10px; }
.op-quick__hd::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: #2b5fd9; }
.op-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 4px; }
.op-tile { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 12px 4px; border-radius: 12px; }
.op-tile:active { background: #f2f6ff; }
.op-tile__ic { position: relative; width: 42px; height: 42px; border-radius: 13px; background: #eef2fb; display: flex; align-items: center; justify-content: center; }
.op-tile__svg svg { stroke: #2b5fd9; }
.op-tile__dot { position: absolute; top: -4px; right: -4px; min-width: 17px; height: 17px; line-height: 17px; padding: 0 4px; border-radius: 9px; background: #ff4d4f; color: #fff; font-size: 10.5px; text-align: center; }
.op-tile__label { font-size: 12.5px; color: #323233; }
.op-tip { font-size: 11.5px; color: #969799; padding: 10px 2px 8px; line-height: 1.6; }

/* 子页 */
/* 【P235】子页顶部返回条（消息中心）：底部 Tab 栏在子页仍显示，这里给一条明确的退出入口 */
.op-sub-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 0.5px solid var(--border, #ebedf0);
  position: sticky;
  top: 0;
  z-index: 12;
}
.op-sub-bar__back { font-size: 18px; color: #323233; }
.op-sub-bar__t { font-size: 15px; font-weight: 500; color: #323233; }
.op-sub-bar__r { margin-left: auto; font-size: 13px; color: #2b5fd9; }

.op-sub { padding-bottom: 20px; }
/* 【P176】内联 Tab 页（房态/快捷入口）：底部要留出 Tab 栏高度，否则内容被 Tab 栏压住 */
.op-tabpage { padding-bottom: 60px; }
.op-date { flex: 1; border: 1px solid var(--border, #ebedf0); border-radius: 8px; padding: 6px 10px; font-size: 13px; color: #323233; background: #fff; }

/* ============================================================================
 * 【P169】房态网格（1:1 移植自销售端 client.vue，仅作用域收窄到 .room-status-page）
 * ========================================================================== */
.room-status-page { background: #f5f7fa; color: #333; -webkit-tap-highlight-color: transparent; }
.room-status-board { display: flex; flex-direction: column; background: #f5f7fa; }
.room-status-board * { margin: 0; padding: 0; box-sizing: border-box; }

/* 顶部日期导航 */
.room-status-page .header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #5b8def 0%, #3b6fd4 100%);
  color: #fff; position: sticky; top: 0; z-index: 99; font-size: 14px;
  box-shadow: 0 2px 8px rgba(59,111,212,.18);
}
.room-status-page .header__date { display: flex; align-items: center; gap: 6px; font-weight: 600; }
.room-status-page .header__cal { font-size: 16px; }
.room-status-page .header__range i { font-style: normal; opacity: .7; margin: 0 2px; }
.room-status-page .header__btn {
  display: flex; align-items: center; gap: 2px;
  background: rgba(255,255,255,.18); border: 1px solid rgba(255,255,255,.35);
  padding: 5px 12px; border-radius: 16px; font-size: 13px;
}

/* 门店横向滚动 */
.room-status-page .store-wrap {
  background: #fff; padding: 10px 12px; overflow-x: auto; white-space: nowrap;
  border-bottom: 1px solid #eee;
}
.room-status-page .store-wrap::-webkit-scrollbar { display: none; }
.room-status-page .store-item {
  display: inline-block; padding: 7px 14px; margin-right: 8px; border-radius: 16px;
  font-size: 13px; background: #eef2f7; color: #5b6472; border: 1px solid transparent;
  transition: all .15s ease;
}
/* 【P176】明确未选中态底色（此前 background 简写被 gradient 规则牵连，实测 backgroundColor 透明） */
.room-status-page .store-item { background-color: #eef2f7; }
.room-status-page .store-item.active {
  background-color: #3b6fd4;
  background-image: linear-gradient(135deg, #5b8def 0%, #3b6fd4 100%);
  color: #fff; border-color: #3b6fd4;
  box-shadow: 0 3px 8px rgba(59,111,212,.28);
}

/* 【P180】图例条样式（.room-legend / .dot--*）已随模板整体删除。
   四色本身由 .content_status_1~4 定义，与图例无关，故保留不动。 */

/* 横向滚动区（日期表头 + 内容同步滚动）
   【P176】高度改为「动态 calc 变量」，由 JS 按实际可用视口计算（见 roomScrollH），
   不再写死 168px —— 此前在无底部 Tab 栏的场景会底部空出 ~53px。 */.room-status-page .scroll-wrapper {
  overflow-x: auto; overflow-y: auto;
  height: var(--room-scroll-h, calc(100vh - 210px));
  overscroll-behavior: none; background: #f5f7fa;
  -webkit-overflow-scrolling: auto;
}
.room-status-page .scroll-wrapper::-webkit-scrollbar { display: none; }
.room-status-page .scroll-content { position: relative; min-height: 100%; }

/* 会议室名列（左固定）
   【P176】行高由「格子撑开」改为「等分铺满」，避免出现 276px 行却有大量空白
   【P180】★ 尺寸 1:1 对齐销售端 client.vue（88px / 13px / padding 10px）：
     此前为提升信息密度压到 72px + 12px，导致左侧房间名被右侧格子「挤压」成窄条，
     用户反馈「左边的标签有问题把信息挤压了」。房间名是固定的短文本（H1/H2/H3），
     不随密度收益而需要压缩，故恢复销售端口径。
     ⚠️ 左侧列宽若与右侧 day-cell 宽度（84px）不成套，滚动时视觉上会「错位」。 */
.room-status-page .room-name {
  position: sticky; left: 0; z-index: 5;
  padding: 10px 10px; border-bottom: 1px solid #eef0f3;
  width: 88px; min-width: 88px;
  font-size: 13px; font-weight: 700; color: #2b3340;
  background: #eef2f7; display: flex; align-items: center;
  box-shadow: 2px 0 6px rgba(0,0,0,.04);
}

/* 日期表头（上固定）
   【P180】列宽 78 → 84，与销售端 .date-item 一致（配合左侧 88px 列宽成套） */
.room-status-page .date-header {
  position: sticky; top: 0; background: #fff; display: flex;
  border-bottom: 1px solid #eef0f3; z-index: 10;
}
.room-status-page .date-item {
  min-width: 84px; padding: 12px 5px; text-align: center;
  font-size: 12px; line-height: 16px; color: #5b6472; border-right: 1px solid #eef0f3;
  flex: 0 0 84px;
}
.room-status-page .date-item.today { color: #3b6fd4; font-weight: 700; background: #eef4ff; }

/* 房态列表 */
.room-status-page .room-list { background: #fff; }
/* 【P181】★★ 关键修复：`.room-row` 必须为 display:block（销售端口径），
   ⚠️ 此前运营端写的是 `display:flex` ⇒ 导致 `.room-name`（sticky/88px）被拉成
      **整行高度的左侧竖列**（实测高 276px），把会议室名 H1/H2 变成左侧竖条，
      与销售端「H1 是横贯整行的**横幅条**、格子在其下方」完全不符。
   ⇒ 改回 block 后：`.room-name` 恢复为一行 38.5px 高的横幅，
      `.room-days`（flex）自然流到其**下方**并从 x=0 起排，与销售端 1:1 一致。 */
.room-status-page .room-row { border-bottom: 1px solid #eef0f3; display: block; }
.room-status-page .room-days { display: flex; }
/* 【P180】day-cell 78 → 84 宽；min-height 58 → 96，对齐销售端 */
.room-status-page .day-cell {
  display: flex; flex-direction: column; align-items: stretch; justify-content: center;
  min-width: 84px; flex: 0 0 84px; padding: 6px 5px;
  border-right: 1px solid #eef0f3; min-height: 96px;
}
/* 【P180】格子高 46 → 82、圆角 8 → 10、padding 6px、阴影对齐销售端。
   ⚠️ status 高（82）必须 ≤ day-cell min-height（96）且留出上下 margin，
      否则 3 段时段的格子会溢出被裁（此前 46px 高正是为塞进 58px 的 day-cell 才压的）。 */
.room-status-page .status {
  display: flex; flex-direction: column; justify-content: center;
  border-radius: 10px; font-size: 12px; margin: 3px 0; text-align: center;
  height: 82px; padding: 8px 6px; box-shadow: 0 2px 6px rgba(0,0,0,.08);
  box-sizing: border-box; overflow: hidden;
}
.room-status-page .status .item_name {
  width: 100%; height: 18px; line-height: 18px;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
}
.room-status-page .status .item_name:first-child { font-weight: 700; font-size: 12px; height: 20px; line-height: 20px; }
.room-status-page .status .item_name:not(:first-child) { opacity: .92; font-size: 11px; height: 16px; line-height: 16px; margin-top: 2px; }
.room-status-page .status_color { font-size: 12px; color: #fff; }
.room-status-page .status--empty {
  background: #f3f5f8; border: 1px dashed #dfe3e8; box-shadow: none; color: #aab2bd;
}
.room-status-page .status--empty__txt { font-size: 11px; }
.room-status-page .content_status_1 { background-color: #409EFF; }
.room-status-page .content_status_2 { background-color: #f59e0b; }
.room-status-page .content_status_3 { background-color: #67C23A; }
.room-status-page .content_status_4 { background-color: #F56C6C; }

/* 【P181】账号条样式（.op-acct*）与退出弹窗样式（.op-logout*）已随模板整体删除。
   历史：P180 为「房态首屏补退出出口」而新增，现用户明确要求去掉名称与退出。 */

/* ============================================================================
 * 【P196】账号 / 退出登录（1:1 对齐销售端 client.vue 的「账号」区块）
 *   ① .acct-row* 全部数值（尺寸/圆角/色值/间距/字号）与 client.vue L8506-8515 逐字一致；
 *   ② 外层容器沿用运营端既有 .op-quick 白卡（与「快捷入口」卡同宽同边距，视觉成组），
 *      销售端用的是自己的 .quick-section--acct，两者卡片外观等价；
 *   ③ .acct-list 左右内边距补到 10px，与销售端 .acct-list{padding:2px 10px 4px} 对齐
 *      ⇒ 使 .acct-row 相对屏幕的左右留白与销售端完全相同。
 * ========================================================================== */
.op-quick--acct { padding: 14px 14px 12px; margin-top: 0; }
/* 销售端标题只有「竖条 + 文字」，运营端 .op-quick__hd 默认是圆点前缀 ⇒ 此处换回竖条 */
.op-quick__hd--plain::before {
  content: ""; width: 3px; height: 14px; border-radius: 2px; background: #2b5fd9;
}
.acct-list { padding: 2px 4px 4px; }
.acct-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 10px; border-radius: 12px;
  background: #fafbfc; border: 1px solid #ebedf0;
}
.acct-row:active { transform: scale(.99); background: #eef2fb; }
.acct-row__ic {
  width: 34px; height: 34px; border-radius: 11px; background: #eef2fb;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.acct-row__ic svg {
  width: 19px; height: 19px; stroke: #2b5fd9; fill: none;
  stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round;
}
.acct-row__txt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.acct-row__label { font-size: 14px; font-weight: 600; color: #323233; }
.acct-row__sub { font-size: 11.5px; color: #969799; }
.acct-row__arrow { color: #c8c9cc; font-size: 14px; flex-shrink: 0; }

/* 房态详情弹窗（专业卡片式） */
.detail-popup { border-radius: 16px 16px 0 0; }
/* 【P170】日期搜索弹窗（与销售端 .dialogDate 同结构，补齐可读样式） */
.dialogDate { width: 100%; padding: 4px 16px 16px; }
.dialogDateRow {
  display: flex; align-items: center; justify-content: space-between;
  min-height: 48px; padding: 0 12px; margin-top: 8px;
  background: #f7f8fa; border-radius: 10px;
}
.dialogDateLable { font-size: 14px; color: #646566; }
.dialogDateValue { font-size: 14px; color: #1d2129; font-weight: 600; }
.dialogDateValue:empty::after { content: '请选择'; color: #c8c9cc; font-weight: 400; }

.detail-popup .van-popup__close-icon { display: none; }
.detail-popup .detail-sheet { display: flex; flex-direction: column; height: 100%; background: #f5f7fa; }
.detail-popup .detail-sheet__hd {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #5b8def 0%, #3b6fd4 100%);
  color: #fff; border-radius: 16px 16px 0 0;
}
.detail-popup .detail-sheet__title { font-size: 16px; font-weight: 700; }
.detail-popup .detail-sheet__close { font-size: 20px; color: #fff; }
.detail-popup .detail-sheet__body {
  flex: 1; overflow-y: auto; padding: 14px 14px 24px;
}
.detail-popup .detail-hero {
  display: flex; align-items: center; background: #fff; border-radius: 14px;
  padding: 14px; box-shadow: 0 2px 10px rgba(0,0,0,.05); margin-bottom: 14px;
}
.detail-popup .detail-hero__main { flex: 1; min-width: 0; }
.detail-popup .detail-hero__name { font-size: 16px; font-weight: 700; color: #1f2733; }
.detail-popup .detail-hero__sub { font-size: 12.5px; color: #8b94a3; margin-top: 3px; }
.detail-popup .detail-hero__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.detail-popup .detail-sec { margin-bottom: 14px; }
.detail-popup .detail-sec__title {
  display: flex; align-items: center; font-size: 14px; font-weight: 700;
  color: #1f2733; margin: 4px 2px 10px;
}
.detail-popup .detail-sec__bar {
  width: 4px; height: 14px; border-radius: 2px;
  background: linear-gradient(180deg, #5b8def, #3b6fd4); margin-right: 8px;
}
.detail-popup .detail-cards { display: flex; flex-direction: column; gap: 10px; }
.detail-popup .detail-card { background: #fff; border-radius: 12px; padding: 12px 14px; box-shadow: 0 2px 8px rgba(0,0,0,.04); }
.detail-popup .detail-card--sm { padding: 10px 12px; }
.detail-popup .detail-card__head { font-size: 14px; font-weight: 700; color: #1f2733; margin-bottom: 8px; }
.detail-popup .detail-card__head em { font-style: normal; font-size: 12px; font-weight: 500; color: #8b94a3; margin-left: 6px; }
.detail-popup .detail-card__row { display: flex; font-size: 13px; padding: 3px 0; line-height: 1.5; }
.detail-popup .detail-card__label { width: 64px; flex-shrink: 0; color: #8b94a3; }
.detail-popup .detail-card__val { flex: 1; color: #2b3340; word-break: break-all; }
.detail-popup .detail-empty { background: #fff; border-radius: 12px; padding: 20px; text-align: center; color: #aab2bd; font-size: 13px; }

/* 服务状态标签 + 底部动作位 */
.op-svc { padding: 5px 12px; border-radius: 14px; font-size: 12px; font-weight: 600; }
.op-svc--0 { background: #f2f3f5; color: #969799; }
.op-svc--1 { background: #fff7e6; color: #d48806; }
.op-svc--2 { background: #e8f3ff; color: #3b6fd4; }
.op-svc--3 { background: #eaf7ea; color: #389e0d; }
.op-svc-ft { display: flex; gap: 10px; padding: 12px 4px 4px; }
.op-svc-ft .van-button { flex: 1; }
</style>



// WEBPACK FOOTER //
// src/pages/operation/index.vue